import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { postWithDupCheck } from "@/lib/dupCheck";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function VoiceAssistant() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const recRef = useRef(null);
  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  if (!user || !["admin", "capocantiere"].includes(user?.role)) return null;

  const start = () => {
    if (!supported) return toast.error("Il tuo browser non supporta il riconoscimento vocale. Prova con Chrome su Android.");
    try {
      const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
      const r = new Rec();
      r.lang = "it-IT";
      r.interimResults = false;
      r.maxAlternatives = 1;
      r.onstart = () => { setListening(true); setTranscript(""); setResult(null); setOpen(true); };
      r.onresult = async (ev) => {
        const text = ev.results?.[0]?.[0]?.transcript || "";
        setTranscript(text);
        setListening(false);
        if (text) await executeCommand(text);
      };
      r.onerror = (e) => { setListening(false); toast.error("Errore vocale: " + (e?.error || "sconosciuto")); };
      r.onend = () => setListening(false);
      recRef.current = r;
      r.start();
    } catch (e) {
      toast.error("Impossibile avviare microfono");
    }
  };

  const stop = () => { try { recRef.current?.stop(); } catch {} setListening(false); };

  const executeCommand = async (text) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/voice-action", { text });
      setResult(data);
      await runAction(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore comando vocale");
    } finally { setBusy(false); }
  };

  const runAction = async (data) => {
    const { action, params = {}, message } = data || {};
    if (action === "navigate" && params.path) {
      nav(params.path);
      toast.success(message || `Vado a ${params.path}`);
      setTimeout(() => setOpen(false), 1500);
      return;
    }
    if (action === "filter_fatture") {
      const sp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") sp.append(k, String(v)); });
      nav(`/fatture?${sp.toString()}`);
      toast.success(message || "Filtri applicati");
      setTimeout(() => setOpen(false), 1500);
      return;
    }
    if (action === "create_fattura") {
      const body = {
        tipo: params.tipo || "acquisto",
        controparte: params.controparte || "",
        numero: params.numero || "",
        data: params.data || new Date().toISOString().slice(0, 10),
        imponibile: Number(params.imponibile || 0),
        iva_percentuale: Number(params.iva_percentuale || 22),
        stato: "da_pagare",
        cantiere_id: params.cantiere_id || null,
        fornitore_id: params.fornitore_id || null,
      };
      if (!window.confirm(`Confermi creazione fattura ${body.tipo} di ${body.imponibile}€ a ${body.controparte}?`)) return;
      try { if (await postWithDupCheck("/fatture", body)) { toast.success("Fattura creata ✓"); setTimeout(() => setOpen(false), 1500); } }
      catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione fattura"); }
      return;
    }
    if (action === "create_ddt") {
      const body = {
        fornitore_id: params.fornitore_id || null,
        cantiere_id: params.cantiere_id || null,
        numero: params.numero || "",
        data: params.data || new Date().toISOString().slice(0, 10),
        descrizione: params.descrizione || "",
        importo_stimato: Number(params.importo_stimato || 0),
      };
      if (!window.confirm(`Confermi creazione DDT: ${body.descrizione || body.numero}?`)) return;
      try { if (await postWithDupCheck("/ddt", body)) { toast.success("DDT creato ✓"); setTimeout(() => setOpen(false), 1500); } }
      catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione DDT"); }
      return;
    }
    toast.info(message || "Comando non riconosciuto. Prova a ripetere.");
  };

  return (
    <>
      <button
        data-testid="voice-mic-btn"
        onClick={() => (listening ? stop() : start())}
        className={`fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-xl border-2 flex items-center justify-center transition-all ${
          listening ? "bg-red-600 border-red-700 animate-pulse" : "bg-primary border-primary/80 hover:scale-105"
        }`}
        title={listening ? "Sto ascoltando..." : "Comando vocale"}
      >
        {busy ? <Loader2 className="h-6 w-6 text-white animate-spin" /> : listening ? <MicOff className="h-6 w-6 text-white" /> : <Mic className="h-6 w-6 text-white" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 bg-card border border-border rounded-xl shadow-2xl p-4" data-testid="voice-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-bold"><Sparkles className="h-4 w-4 text-primary" /> Assistente vocale</div>
            <button onClick={() => setOpen(false)}><X className="h-4 w-4 text-muted-foreground" /></button>
          </div>
          {listening && <div className="text-xs text-muted-foreground">🎤 Sto ascoltando... parla ora.</div>}
          {transcript && (
            <div className="mt-2 bg-secondary rounded-lg p-2 text-xs" data-testid="voice-transcript">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Hai detto</div>
              <div className="font-medium">{transcript}</div>
            </div>
          )}
          {busy && <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Interpreto...</div>}
          {result && (
            <div className="mt-2 bg-primary/5 border border-primary/30 rounded-lg p-2 text-xs" data-testid="voice-result">
              <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Azione: {result.action}</div>
              <div>{result.message}</div>
            </div>
          )}
          <div className="mt-3 text-[10px] text-muted-foreground">
            Prova: "Mostrami i cantieri", "Crea fattura di 500 euro a Rossi", "Filtra fatture di marzo", "Vai al magazzino".
          </div>
        </div>
      )}
    </>
  );
}
